import { X } from "lucide-react";
import { useEffect, type ReactNode } from "react";

export function Modal({
  open,
  title,
  onClose,
  children,
  size = "md",
  variant = "dialog",
  ariaLabel
}: {
  open: boolean;
  title?: string;
  onClose: () => void;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
  variant?: "dialog" | "drawer";
  ariaLabel?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const maxWidth = size === "sm" ? "max-w-sm" : size === "lg" ? "max-w-2xl" : "max-w-md";

  const isDrawer = variant === "drawer";

  return (
    <div
      className={`fixed inset-0 z-50 flex bg-black/50 ${isDrawer ? "justify-end" : "items-center justify-center p-4"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
      aria-label={title ? undefined : ariaLabel}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={`surface ${isDrawer ? "modal-drawer-content h-full w-full max-w-xl overflow-y-auto p-5" : `modal-content relative w-full ${maxWidth} p-5`}`}>
        {title ? (
          <div className="mb-4 flex items-start justify-between gap-3">
            <h3 id="modal-title" className="text-base font-bold">
              {title}
            </h3>
            <button
              type="button"
              className="rounded-md p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="absolute right-4 top-4 rounded-md p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        )}
        {children}
      </div>
    </div>
  );
}
